import React, { useState, useEffect } from 'react';
import {
  User,
  Building,
  MapPin,
  Calendar,
  DollarSign,
  AlertCircle,
  Loader,
  CheckCircle,
} from 'lucide-react';
import { Input, Dropdown, TextArea, DatePicker } from '../forms';
import { CreateManpowerLogData, Project } from '../../types';
import manpowerLogService from '../../services/manpowerLogService';

interface ManpowerLogFormProps {
  onSubmit: (data: CreateManpowerLogData) => void;
  onCancel: () => void;
  projects: Project[];
  isSubmitting: boolean;
  initialData?: Partial<CreateManpowerLogData>;
  editMode?: boolean;
}

const ManpowerLogForm: React.FC<ManpowerLogFormProps> = ({
  onSubmit,
  onCancel,
  projects,
  isSubmitting,
  initialData,
  editMode = false,
}) => {
  const [entryType, setEntryType] = useState<'worker' | 'company'>('worker');
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [workerRoles, setWorkerRoles] = useState<{ value: string; label: string }[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [formData, setFormData] = useState<CreateManpowerLogData>({
    project_id: initialData?.project_id || '',
    worker_id: initialData?.worker_id || '',
    worker_name: initialData?.worker_name || '',
    company_id: initialData?.company_id || '',
    company_name: initialData?.company_name || '',
    number_of_workers: initialData?.number_of_workers || 1,
    role: initialData?.role || '',
    location: {
      site: initialData?.location?.site || '',
      building: initialData?.location?.building || '',
      floor: initialData?.location?.floor || '',
      room: initialData?.location?.room || '',
      gps: initialData?.location?.gps || '',
    },
    date: initialData?.date || new Date().toISOString().split('T')[0],
    hours_worked: initialData?.hours_worked || 8,
    overtime_hours: initialData?.overtime_hours || 0,
    hourly_rate: initialData?.hourly_rate || 0,
    overtime_rate: initialData?.overtime_rate || 0,
    payment_notes: initialData?.payment_notes || '',
  });

  // Determine initial entry type based on initial data
  useEffect(() => {
    if (initialData?.worker_id && initialData?.worker_name) {
      setEntryType('worker');
    } else if (initialData?.company_id && initialData?.company_name) {
      setEntryType('company');
    }
  }, [initialData]);

  // Load dropdown options
  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [companiesData, rolesData] = await Promise.all([
          manpowerLogService.getCompaniesForDropdown(),
          manpowerLogService.getWorkerRoles(),
        ]);
        setCompanies(companiesData);
        setWorkerRoles(rolesData);
      } catch (error) {
        console.error('Failed to load options:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  // Calculate overtime rate automatically (1.5x regular rate)
  useEffect(() => {
    if (formData.hourly_rate > 0) {
      setFormData(prev => ({
        ...prev,
        overtime_rate: prev.hourly_rate * 1.5,
      }));
    }
  }, [formData.hourly_rate]);

  // Clear worker/company data when switching entry type
  useEffect(() => {
    if (entryType === 'worker') {
      setFormData(prev => ({
        ...prev,
        company_id: '',
        company_name: '',
        number_of_workers: 1, // Always 1 for individual workers
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        worker_id: '',
        worker_name: '',
        number_of_workers: prev.number_of_workers || 1, // Keep existing value or default to 1
      }));
    }
  }, [entryType]);

  const handleInputChange = (field: keyof CreateManpowerLogData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleLocationChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: value,
      },
    }));
  };

  const handleCompanySelect = (companyId: string) => {
    const selectedCompany = companies.find(c => c.value === companyId);
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      company_name: selectedCompany?.label || '',
    }));
  };

  const calculateTotalPayment = () => {
    return manpowerLogService.calculateTotalPayment(formData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    const validationErrors = manpowerLogService.validateLogData(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Submit form
    onSubmit(formData);
  };

  const projectOptions = projects.map(p => ({ value: p.id, label: p.name }));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Display */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Entry Type Selection - Enhanced */}
      <div className="bg-gradient-to-r from-brand-50 to-indigo-50 rounded-xl p-6 border border-brand-200">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Entry Type</h3>
          <p className="text-sm text-gray-600">
            Select whether you're logging hours for an individual worker or a company team
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setEntryType('worker')}
            className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
              entryType === 'worker'
                ? 'border-red-500 bg-red-50 shadow-lg scale-105'
                : 'border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className={`p-3 rounded-full mb-3 ${
                  entryType === 'worker' ? 'bg-red-100' : 'bg-gray-100'
                }`}
              >
                <User
                  className={`h-6 w-6 ${entryType === 'worker' ? 'text-red-600' : 'text-gray-600'}`}
                />
              </div>
              <h4
                className={`font-semibold mb-2 ${
                  entryType === 'worker' ? 'text-red-700' : 'text-gray-700'
                }`}
              >
                Individual Worker
              </h4>
              <p className={`text-sm ${entryType === 'worker' ? 'text-red-600' : 'text-gray-500'}`}>
                Log hours for a specific worker with worker ID and name
              </p>
            </div>
            {entryType === 'worker' && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                <CheckCircle className="h-4 w-4" />
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => setEntryType('company')}
            className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
              entryType === 'company'
                ? 'border-red-500 bg-red-50 shadow-lg scale-105'
                : 'border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className={`p-3 rounded-full mb-3 ${
                  entryType === 'company' ? 'bg-red-100' : 'bg-gray-100'
                }`}
              >
                <Building
                  className={`h-6 w-6 ${
                    entryType === 'company' ? 'text-red-600' : 'text-gray-600'
                  }`}
                />
              </div>
              <h4
                className={`font-semibold mb-2 ${
                  entryType === 'company' ? 'text-red-700' : 'text-gray-700'
                }`}
              >
                Company Team
              </h4>
              <p
                className={`text-sm ${entryType === 'company' ? 'text-red-600' : 'text-gray-500'}`}
              >
                Log hours for a company team with multiple workers
              </p>
            </div>
            {entryType === 'company' && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                <CheckCircle className="h-4 w-4" />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Dropdown
          label="Project *"
          value={formData.project_id}
          onChange={value => handleInputChange('project_id', value)}
          options={projectOptions}
          placeholder="Select project"
          required
        />

        <Dropdown
          label="Role *"
          value={formData.role}
          onChange={value => handleInputChange('role', value)}
          options={workerRoles}
          placeholder="Select role"
          required
          disabled={loadingOptions}
        />
      </div>

      {/* Worker/Company Details - Enhanced */}
      <div
        className={`rounded-xl border-2 p-6 transition-all duration-200 ${
          entryType === 'worker' ? 'border-brand-200 bg-brand-50' : 'border-purple-200 bg-purple-50'
        }`}
      >
        <div className="flex items-center mb-4">
          <div
            className={`p-2 rounded-lg mr-3 ${
              entryType === 'worker' ? 'bg-brand-100' : 'bg-purple-100'
            }`}
          >
            {entryType === 'worker' ? (
              <User
                className={`h-5 w-5 ${entryType === 'worker' ? 'text-brand-primary' : 'text-purple-600'}`}
              />
            ) : (
              <Building className="h-5 w-5 text-purple-600" />
            )}
          </div>
          <div>
            <h3
              className={`text-lg font-semibold ${
                entryType === 'worker' ? 'text-brand-900' : 'text-purple-900'
              }`}
            >
              {entryType === 'worker' ? 'Individual Worker Details' : 'Company Team Details'}
            </h3>
            <p
              className={`text-sm ${entryType === 'worker' ? 'text-brand-700' : 'text-purple-700'}`}
            >
              {entryType === 'worker'
                ? 'Enter the specific worker information'
                : 'Select company and specify team size'}
            </p>
          </div>
        </div>

        {entryType === 'worker' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Worker ID *"
                value={formData.worker_id || ''}
                onChange={e => handleInputChange('worker_id', e.target.value)}
                placeholder="e.g., WR-1005"
                required
              />
              <Input
                label="Worker Name *"
                value={formData.worker_name || ''}
                onChange={e => handleInputChange('worker_name', e.target.value)}
                placeholder="e.g., John Smith"
                required
              />
            </div>
            {/* Visual indicator for single worker */}
            <div className="bg-brand-100 border border-brand-200 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center text-brand-700">
                <User className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Single Worker Entry</span>
              </div>
              <p className="text-xs text-brand-primary mt-1">
                Hours will be logged for one individual worker
              </p>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Dropdown
              label="Company *"
              value={formData.company_id || ''}
              onChange={handleCompanySelect}
              options={companies}
              placeholder="Select company"
              required
              disabled={loadingOptions}
            />
            <Input
              label="Number of Workers *"
              type="number"
              value={formData.number_of_workers}
              onChange={e => handleInputChange('number_of_workers', parseInt(e.target.value) || 1)}
              min="1"
              required
              placeholder="Enter team size"
            />
          </div>
        )}
      </div>

      {/* Location Details */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900 flex items-center">
          <MapPin className="h-4 w-4 mr-2" />
          Location Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Site *"
            value={formData.location.site}
            onChange={e => handleLocationChange('site', e.target.value)}
            placeholder="e.g., Site A"
            required
          />
          <Input
            label="Building"
            value={formData.location.building || ''}
            onChange={e => handleLocationChange('building', e.target.value)}
            placeholder="e.g., Tower 1"
          />
          <Input
            label="Floor"
            value={formData.location.floor || ''}
            onChange={e => handleLocationChange('floor', e.target.value)}
            placeholder="e.g., L2"
          />
          <Input
            label="Room"
            value={formData.location.room || ''}
            onChange={e => handleLocationChange('room', e.target.value)}
            placeholder="e.g., Room 201"
          />
        </div>
        <Input
          label="GPS Coordinates"
          value={formData.location.gps || ''}
          onChange={e => handleLocationChange('gps', e.target.value)}
          placeholder="e.g., 25.2048, 55.2708"
        />
      </div>

      {/* Date and Time */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900 flex items-center">
          <Calendar className="h-4 w-4 mr-2" />
          Date and Hours
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DatePicker
            label="Work Date *"
            value={formData.date}
            onChange={date => handleInputChange('date', date || '')}
            required
          />
          <Input
            label="Hours Worked *"
            type="number"
            value={formData.hours_worked}
            onChange={e => handleInputChange('hours_worked', parseFloat(e.target.value) || 0)}
            min="0"
            max="24"
            step="0.5"
            required
          />
          <Input
            label="Overtime Hours"
            type="number"
            value={formData.overtime_hours}
            onChange={e => handleInputChange('overtime_hours', parseFloat(e.target.value) || 0)}
            min="0"
            max="12"
            step="0.5"
          />
        </div>
      </div>

      {/* Payment Information */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900 flex items-center">
          <DollarSign className="h-4 w-4 mr-2" />
          Payment Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Hourly Rate *"
            type="number"
            value={formData.hourly_rate}
            onChange={e => handleInputChange('hourly_rate', parseFloat(e.target.value) || 0)}
            min="0"
            step="0.01"
            placeholder="0.00"
            required
          />
          <Input
            label="Overtime Rate"
            type="number"
            value={formData.overtime_rate}
            onChange={e => handleInputChange('overtime_rate', parseFloat(e.target.value) || 0)}
            min="0"
            step="0.01"
            placeholder="Auto-calculated (1.5x)"
            disabled
          />
        </div>

        {/* Payment Calculation Preview - Enhanced */}
        <div
          className={`rounded-lg p-6 border-2 ${
            entryType === 'worker'
              ? 'bg-green-50 border-green-200'
              : 'bg-yellow-50 border-yellow-200'
          }`}
        >
          <div className="flex items-center mb-4">
            <DollarSign
              className={`h-5 w-5 mr-2 ${
                entryType === 'worker' ? 'text-green-600' : 'text-yellow-600'
              }`}
            />
            <h4
              className={`text-lg font-semibold ${
                entryType === 'worker' ? 'text-green-900' : 'text-yellow-900'
              }`}
            >
              {entryType === 'worker'
                ? 'Worker Payment Calculation'
                : 'Company Team Payment Calculation'}
            </h4>
          </div>

          {/* Calculation Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-lg p-3">
              <span
                className={`text-sm font-medium ${
                  entryType === 'worker' ? 'text-green-700' : 'text-yellow-700'
                }`}
              >
                Regular Pay:
              </span>
              <div className="text-lg font-bold text-gray-900">
                {manpowerLogService.formatCurrency(
                  formData.number_of_workers * formData.hours_worked * formData.hourly_rate
                )}
              </div>
              <div className="text-xs text-gray-500">
                {formData.number_of_workers} × {formData.hours_worked}h ×{' '}
                {manpowerLogService.formatCurrency(formData.hourly_rate)}
              </div>
            </div>

            <div className="bg-white rounded-lg p-3">
              <span
                className={`text-sm font-medium ${
                  entryType === 'worker' ? 'text-green-700' : 'text-yellow-700'
                }`}
              >
                Overtime Pay:
              </span>
              <div className="text-lg font-bold text-gray-900">
                {manpowerLogService.formatCurrency(
                  formData.number_of_workers * formData.overtime_hours * formData.overtime_rate
                )}
              </div>
              <div className="text-xs text-gray-500">
                {formData.number_of_workers} × {formData.overtime_hours}h ×{' '}
                {manpowerLogService.formatCurrency(formData.overtime_rate)}
              </div>
            </div>

            <div className="bg-white rounded-lg p-3">
              <span
                className={`text-sm font-medium ${
                  entryType === 'worker' ? 'text-green-700' : 'text-yellow-700'
                }`}
              >
                {entryType === 'worker' ? 'Worker' : 'Team Size'}:
              </span>
              <div className="text-lg font-bold text-gray-900">{formData.number_of_workers}</div>
              <div className="text-xs text-gray-500">
                {entryType === 'worker' ? 'Individual' : `${formData.number_of_workers} workers`}
              </div>
            </div>

            <div
              className={`rounded-lg p-3 ${
                entryType === 'worker'
                  ? 'bg-green-100 border border-green-300'
                  : 'bg-yellow-100 border border-yellow-300'
              }`}
            >
              <span
                className={`text-sm font-medium ${
                  entryType === 'worker' ? 'text-green-800' : 'text-yellow-800'
                }`}
              >
                Total Payment:
              </span>
              <div
                className={`text-xl font-bold ${
                  entryType === 'worker' ? 'text-green-900' : 'text-yellow-900'
                }`}
              >
                {manpowerLogService.formatCurrency(calculateTotalPayment())}
              </div>
              <div className="text-xs text-gray-600">All inclusive</div>
            </div>
          </div>

          {/* Cost per hour/worker insights */}
          {formData.hours_worked > 0 && (
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Cost per hour:</span>
                <span className="font-semibold text-gray-900">
                  {manpowerLogService.formatCurrency(
                    calculateTotalPayment() /
                      ((formData.hours_worked + formData.overtime_hours) *
                        formData.number_of_workers)
                  )}
                </span>
              </div>
              {entryType === 'company' && (
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="text-gray-600">Cost per worker:</span>
                  <span className="font-semibold text-gray-900">
                    {manpowerLogService.formatCurrency(
                      calculateTotalPayment() / formData.number_of_workers
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Notes */}
      <TextArea
        label="Payment Notes"
        value={formData.payment_notes || ''}
        onChange={e => handleInputChange('payment_notes', e.target.value)}
        placeholder="Optional notes about payment, adjustments, or special instructions..."
        rows={3}
      />

      {/* Action Buttons */}
      <div className="border-t border-gray-200 pt-6">
        {/* Entry Summary */}
        <div
          className={`rounded-lg p-4 mb-4 border-2 ${
            entryType === 'worker'
              ? 'bg-brand-50 border-brand-200'
              : 'bg-purple-50 border-purple-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {entryType === 'worker' ? (
                <User className="h-5 w-5 text-brand-primary mr-2" />
              ) : (
                <Building className="h-5 w-5 text-purple-600 mr-2" />
              )}
              <div>
                <p
                  className={`font-medium ${
                    entryType === 'worker' ? 'text-brand-900' : 'text-purple-900'
                  }`}
                >
                  {entryType === 'worker' ? 'Individual Worker Entry' : 'Company Team Entry'}
                </p>
                <p
                  className={`text-sm ${
                    entryType === 'worker' ? 'text-brand-700' : 'text-purple-700'
                  }`}
                >
                  {entryType === 'worker'
                    ? `Worker: ${formData.worker_name || 'Not specified'}`
                    : `Company: ${formData.company_name || 'Not selected'} (${formData.number_of_workers} workers)`}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Cost</p>
              <p className="text-lg font-bold text-gray-900">
                {manpowerLogService.formatCurrency(calculateTotalPayment())}
              </p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || loadingOptions}
            className={`inline-flex items-center px-6 py-2 text-sm font-medium text-white border border-transparent rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
              entryType === 'worker'
                ? 'bg-brand-500 hover:bg-brand-primary'
                : 'bg-purple-500 hover:bg-purple-600'
            }`}
          >
            {isSubmitting && <Loader className="animate-spin h-4 w-4 mr-2" />}
            {entryType === 'worker' ? (
              <User className="h-4 w-4 mr-2" />
            ) : (
              <Building className="h-4 w-4 mr-2" />
            )}
            {editMode ? 'Update' : 'Create'} {entryType === 'worker' ? 'Worker' : 'Company'} Entry
          </button>
        </div>
      </div>
    </form>
  );
};

export default ManpowerLogForm;
